import axios from 'axios';
import { authHeader } from '../helpers';
import { Roles } from '../../helpers/roles';

const axiosInstance = axios.create({
  baseURL: ''
});

// 🔥 GLOBAL RESPONSE HANDLER
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      localStorage.removeItem('user');

      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }

      // ✅ STOP propagation completely
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);

export const clientsService = {
  getClients,
  getAdvisorClients,
  getPracticeClients,
  getClientAccounts,
  getClientAccountFunds,
  getAccountHistoricBalances,
  getPortfolioHistoricBalances
};

// 🔥 Shared payload builder (handles string + object)
function buildClientPayload(filter) {
  if (typeof filter === 'string') {
    return {
      filter: filter || '',
      pageIndex: 0
    };
  }

  return {
    filter: filter?.term || '',
    provider: filter?.provider || null,
    pageIndex: filter?.pageIndex ?? 0
  };
}

// --- GET CLIENTS ---
async function getClients(filter = {}) {
  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader()
    },
    withCredentials: true
  };

  const payload = buildClientPayload(filter);

  const response = await axiosInstance.post('/api/dataview/clients', payload, requestOptions);

  return response.data;
}

// --- GET PRACTICE CLIENTS ---
async function getPracticeClients(filter = {}) {
  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(Roles.Management)
    },
    withCredentials: true
  };

  const payload = buildClientPayload(filter);

  const response = await axiosInstance.post('/api/dataview/clients', payload, requestOptions);

  return response.data;
}

// --- GET ADVISOR CLIENTS ---
async function getAdvisorClients(filter = {}) {
  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(Roles.WealthManager)
    },
    withCredentials: true
  };

  const payload = buildClientPayload(filter);

  const response = await axiosInstance.post('/api/dataview/clients', payload, requestOptions);

  return response.data;
}

// --- GET CLIENT ACCOUNTS ---
async function getClientAccounts(clientId, asof) {
  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader()
    },
    withCredentials: true
  };

  const response = await axiosInstance.post(
    '/api/dataview/client-accounts',
    {
      id: clientId,
      asof
    },
    requestOptions
  );

  return response.data;
}

// --- GET CLIENT ACCOUNT FUNDS ---
async function getClientAccountFunds({ id, asof }) {
  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader()
    },
    withCredentials: true
  };

  const response = await axiosInstance.post(
    '/api/dataview/client-account-funds',
    {
      id,
      asof
    },
    requestOptions
  );

  return response.data;
}

// --- GET ACCOUNT HISTORIC BALANCES ---
async function getAccountHistoricBalances({ accountIds, fromDate, toDate }) {
  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader()
    },
    withCredentials: true
  };

  const response = await axiosInstance.post(
    '/api/dataview/client-account-historic-balances',
    {
      accountIds,
      fromDate,
      toDate
    },
    requestOptions
  );

  return response.data;
}

// --- GET PORTFOLIO HISTORIC BALANCES ---
async function getPortfolioHistoricBalances({ clientId, fromDate, toDate }) {
  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeader()
    },
    withCredentials: true
  };

  const response = await axiosInstance.post(
    '/api/dataview/portfolio-historic-balances',
    {
      clientId,
      fromDate,
      toDate
    },
    requestOptions
  );

  return response.data;
}
